// eslint-disable-next-line no-restricted-imports -- This background query runs in entry points without ReportErrorContextProvider.
import {reportError} from '@github-ui/failbot'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {useQuery} from '@github-ui/react-query'
import {useEffect} from 'react'

import {
  fetchIssueFormSecondary,
  issueFormSecondaryQueryKey,
  type IssueFormSecondaryRepository,
} from './fetch-issue-form-secondary'

type useIssueFormSecondaryQueryProps = {
  owner: string
  repo: string
}

export type SecondaryQueryData = {
  securityPolicyUrl: string | null
  contributingFileUrl: string | null
  codeOfConductFileUrl: string | null
  supportFileUrl: string | null
  orgPinnedFields: readonly IssueFieldSelection[] | null
  fieldVisibilityMap: Record<string, string>
  orgHasIssueFields: boolean
  orgHasIssueTypes: boolean
}

const SAFE_DEFAULTS: SecondaryQueryData = {
  securityPolicyUrl: null,
  contributingFileUrl: null,
  codeOfConductFileUrl: null,
  supportFileUrl: null,
  orgPinnedFields: null,
  fieldVisibilityMap: {},
  orgHasIssueFields: true,
  orgHasIssueTypes: true,
}

type OwnerWithIssueTypes = {
  readonly issueTypes?: {
    readonly totalCount: number
  } | null
}

function hasIssueTypes(owner: object): owner is OwnerWithIssueTypes {
  return 'issueTypes' in owner
}

function mapSecondaryQueryData(repository: IssueFormSecondaryRepository): SecondaryQueryData {
  const owner = 'owner' in repository && repository.owner ? repository.owner : null
  const organization = owner?.__typename === 'Organization' ? owner : null
  const pinnedIssueFieldNodes = organization?.pinnedIssueFields?.nodes
  const orgPinnedFields = parsePinnedIssueFields(pinnedIssueFieldNodes ?? undefined)

  const fieldVisibilityMap: Record<string, string> = {}
  for (const node of organization?.issueFields?.nodes ?? []) {
    if (node && 'id' in node && 'visibility' in node) {
      fieldVisibilityMap[node.id] = node.visibility
    }
  }

  return {
    securityPolicyUrl: repository.securityPolicyUrl ?? null,
    contributingFileUrl: repository.contributingFileUrl ?? null,
    codeOfConductFileUrl: repository.codeOfConductFileUrl ?? null,
    supportFileUrl: repository.supportFileUrl ?? null,
    orgPinnedFields,
    fieldVisibilityMap,
    orgHasIssueFields: (organization?.issueFields?.totalCount ?? 0) > 0,
    orgHasIssueTypes: (owner && hasIssueTypes(owner) ? (owner.issueTypes?.totalCount ?? 0) : 0) > 0,
  }
}

export function useIssueFormSecondaryQuery({owner, repo}: useIssueFormSecondaryQueryProps): SecondaryQueryData | null {
  const serverQuery = useQuery({
    queryKey: issueFormSecondaryQueryKey(owner, repo),
    queryFn: ({signal}) => fetchIssueFormSecondary(owner, repo, signal),
    select: mapSecondaryQueryData,
    retry: false,
  })

  useEffect(() => {
    if (serverQuery.error) reportError(serverQuery.error)
  }, [serverQuery.error])

  if (serverQuery.isError) return SAFE_DEFAULTS
  return serverQuery.data ?? null
}
